import { ChangeDetectionStrategy, Component, HostBinding, ViewEncapsulation, computed, input } from '@angular/core';
import { CoreComponent } from '../core/core.component.js';
import { FlexSize } from '../flex/index.js';
import { IconType } from '../icons/index.js';
import { TooltipLocation } from '../tooltip/tooltip.directive.js';
import { IButtonNested } from './button.models.js';

export type ButtonType = 'toolbar' | 'primary' | 'primaryLight' | 'basic' | 'delete' | 'disabled' | 'mobileToolbar';
export type ButtonSize = 'default' | 'large' | 'full';
export type ButtonIconLocation = 'before' | 'after';

@Component({
	selector: 'lib-button',
	templateUrl: './button.component.html',
	styleUrls: ['./button.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None, // needed for ripple effect
	standalone: false
})
export class ButtonComponent extends CoreComponent {
	public readonly badge = input<string | number>();
	public readonly routerLink = input<string>();
	public readonly tooltip = input<string>();
	public readonly tooltipLocation = input<TooltipLocation>();
	public readonly icon = input<IconType>();
	public readonly buttonType = input<ButtonType>();
	public readonly buttonSize = input<ButtonSize>();
	public readonly iconOnly = input<boolean>(false);
	public readonly disabled = input<boolean>(false);
	public readonly hidden = input<boolean>(false);
	public readonly nestedButtons = input<readonly IButtonNested[]>();
	public readonly iconLocation = input<ButtonIconLocation>();
	public readonly disabledButtonType = input<ButtonType>('disabled');

	@HostBinding('style') override style = 'display: block';

	public readonly calculatedIconLocation = computed<ButtonIconLocation>(() => {
		const iconLocation = this.iconLocation();

		if (!iconLocation) {
			return 'before';
		}

		return iconLocation;
	});

	public readonly calculatedButtonType = computed<ButtonType>(() => {
		if (this.disabled()) {
			return this.disabledButtonType();
		}

		return this.buttonType() ?? 'basic';
	});

	public readonly baseColumnSize = computed<FlexSize | undefined>(() => {
		if (this.isMobileView() || this.buttonSize() === 'full') {
			return 'fill';
		}
		return undefined;
	});

	constructor() {
		super();
	}

	handleButtonClick(element: HTMLElement, event: MouseEvent): void {
		this.addRippleEffect(element, event);

		if (this.disabled()) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handleNestedButtonClick(event: Event, nestedButton: IButtonNested): void {
		if (this.disabled()) {
			event.stopPropagation();
			event.preventDefault();
		}

		nestedButton.click?.();

		if (nestedButton.clickAsync) {
			super.subscribeToObservable(nestedButton.clickAsync());
		}
	}

	private addRippleEffect(element: HTMLElement, event: MouseEvent): void {
		// Setup
		const posX = element.offsetLeft;
		const posY = element.offsetTop;
		let buttonWidth = element.offsetWidth;
		let buttonHeight = element.offsetHeight;

		// Add the element
		const ripple = document.createElement('span');
		ripple.className = 'ripple';

		element.appendChild(ripple);

		// Make it round!
		if (buttonWidth >= buttonHeight) {
			buttonHeight = buttonWidth;
		} else {
			buttonWidth = buttonHeight;
		}

		// Get the center of the element
		const x = event.pageX - posX - buttonWidth / 2;
		const y = event.pageY - posY - buttonHeight / 2;

		ripple.style.width = `${buttonWidth}px`;
		ripple.style.height = `${buttonHeight}px`;
		ripple.style.top = `${y}px`;
		ripple.style.left = `${x}px`;

		ripple.classList.add('rippleAnimation');

		setTimeout(() => {
			element.removeChild(ripple);
		}, 1000);
	}
}
